/**
 * MarketplaceOrgNameModal
 *
 * Shown once to the Marketplace purchaser (trainer) after their first claim.
 * Lets them confirm or change the auto-generated organisation name before
 * they start using the platform.
 *
 * Dismissed only on a successful rename — skipping is not allowed so every
 * org ends up with a recognisable name.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { getBackendUrl } from '../../utils/utils';
import { useAuth } from '../../contexts/AuthContext';
import './MarketplaceOrgNameModal.scss';

interface MarketplaceOrgNameModalProps {
  /** Current (auto-generated) org name pre-filled in the input. */
  currentOrgName?: string;
  /** Called after a successful rename so the parent can refresh auth state. */
  onConfirmed: () => void;
}

const MarketplaceOrgNameModal: React.FC<MarketplaceOrgNameModalProps> = ({
  currentOrgName = '',
  onConfirmed,
}) => {
  const { token, refreshAuth } = useAuth();
  const [orgName, setOrgName] = useState(currentOrgName);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = orgName.trim();
    if (!trimmed) {
      setError('Organisation name is required.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.put(
        `${getBackendUrl()}/marketplace/org-name`,
        { org_name: trimmed },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Refresh auth so orgNamePending is cleared and userData reflects the new name.
      await refreshAuth();
      onConfirmed();
    } catch (err: any) {
      const detail =
        err.response?.data?.detail ||
        err.message ||
        'Failed to update organisation name. Please try again.';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mp-org-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="mp-org-modal-title">
      <div className="mp-org-modal">
        <div className="mp-org-modal__header">
          <img
            src="/assets/branding/rc-logo-dark.png"
            alt="Reality Coach"
            className="mp-org-modal__logo"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        </div>

        <h2 id="mp-org-modal-title" className="mp-org-modal__title">
          Welcome to Reality Coach!
        </h2>
        <p className="mp-org-modal__subtitle">
          Please confirm or enter your organisation name.
        </p>

        <form onSubmit={handleSubmit} className="mp-org-modal__form">
          <label htmlFor="mp-org-name-input" className="mp-org-modal__label">
            Organisation Name
          </label>
          <input
            id="mp-org-name-input"
            type="text"
            className="mp-org-modal__input"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            placeholder="e.g. Acme Corp"
            autoFocus
            maxLength={255}
            disabled={loading}
          />

          {error && (
            <p className="mp-org-modal__error" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            className="mp-org-modal__submit"
            disabled={loading || !orgName.trim()}
          >
            {loading ? 'Saving…' : 'Confirm Organisation Name'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default MarketplaceOrgNameModal;
